

































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";
import i18n from "@/i18n";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import AdminBrands from "@/mixins/adminBrands";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import { IUser, IUserForm } from "@/models/users";
import { IBrandCheckable } from "@/models/brands";
import { IUserGroup } from "@/models/userGroups";

@Component({
  computed: {
    ...mapState({
      userGroups(state: IState, getters) {
        return getters[`data/list`]({
          storeModule: DataModules.USER_GROUPS,
          scope: this.userGroupsScope
        });
      },
      userGroupsScope(state: IState, getters) {
        return getters[`data/${DataModules.USER_GROUPS}/userGroupsScope`];
      }
    })
  }
})
export default class UserPermissions extends Mixins(
  RequestPromises,
  AdminBrands
) {
  // Map State

  public userGroups!: IUserGroup[];
  public userGroupsScope!: string;

  // Props

  @Prop({ required: true })
  public value!: IUserForm;

  @Prop({ required: true })
  public user!: IUser;

  @Prop({ required: true })
  public isProcessing!: boolean;

  @Prop({ required: true })
  public isDisabled!: boolean;

  @Prop({ default: i18n.t("_.api_token") })
  public userLabel!: string;

  @Prop({ default: false })
  public isApiToken!: boolean;

  // Data

  public isLoading = true;
  public isReloading = false;
  public requestError = false;

  public userBrands: IBrandCheckable[] = [];
  public userBrandGroup = {};
  public initialUserBrandGroup = {};

  // Getters

  public get form() {
    return this.value;
  }

  // Setters

  public set form(value) {
    this.$emit("input", value);
  }

  // Life-cycle methods

  public async created() {
    await this.loadData();

    this.$bus.$on("admin/userGroups/save", () => {
      this.isLoading = true;
      this.loadData(true);
    });
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.USER_GROUPS,
      scope: this.userGroupsScope,
      vm: this
    });

    this.$bus.$off("admin/userGroups/save", this.reloadData);
  }

  // Methods

  public async loadData(ignoreStored = false) {
    try {
      await this.getUserGroups(ignoreStored);
      this.initiateUserPermissions();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData(this.isReloading);
  }

  public getUserGroups(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.USER_GROUPS}/list`, {
      scope: this.userGroupsScope,
      params: { limit: 0 },
      vm: this,
      ignoreStored
    });
  }

  public initiateUserPermissions() {
    this.userBrands = _.map(this.brands, brand =>
      _.merge(brand, { is_checked: false })
    );

    this.userBrandGroup = _.mapValues(_.keyBy(this.userBrands, "id"), brand => {
      const filteredUserBrandGroups = _.filter(
        _.get(this.user, "user_brand_group", []),
        ["brand_id", brand.id]
      );

      if (filteredUserBrandGroups.length) {
        this.userBrands[
          _.findIndex(this.userBrands, ["id", brand.id])
        ].is_checked = true;
        return _.map(filteredUserBrandGroups, group => group.group_id);
      }

      return [];
    });

    this.setInitialState();
  }

  public setInitialState() {
    this.initialUserBrandGroup = _.cloneDeep(this.userBrandGroup);
  }

  public revertToInitialState() {
    this.userBrandGroup = _.cloneDeep(this.initialUserBrandGroup);
    this.userBrands = _.map(this.brands, brand =>
      _.merge(brand, { is_checked: !!this.userBrandGroup[brand.id].length })
    );
  }

  public toggleBrand(brand: IBrandCheckable): void {
    if (!brand.is_checked) {
      this.$_.set(this.userBrandGroup, brand.id, []);
    }
    this.$forceUpdate();
  }

  // Watchers

  @Watch("userBrandGroup", { deep: true })
  public updateFormValues() {
    this.$set(
      this.form,
      "user_brand_group",
      _.flatten(
        _.map(this.userBrandGroup, (groupValues, brandId) => {
          return _.map(groupValues, groupId => {
            return { brand_id: brandId, group_id: groupId };
          });
        })
      )
    );
  }
}
